
import { computed, defineComponent, onMounted, Ref, ref } from "vue";
import { useRoute } from "vue-router";

import { AppHelpers } from "@/core/modules/helpers/AppHelpers";
import { DataHelpers } from "@/core/modules/helpers/DataHelpers";
import { FirestoreDocument } from "@/core/modules/firestore/objects/FirestoreDocument";
import { KoruSortableBrowserModule } from "../objects/KoruSortableBrowserModule";
import { Navigation } from "@/core/modules/navigation/objects/Navigation";
import { ContextCommand } from "@/core/modules/layout/interfaces/ContextCommand";
import { storeTypes } from "@/core/modules/store/types";
import { ToastHelpers } from "@/core/modules/helpers/ToastHelpers";
import { useLocale } from "@/core/modules/locale/module";
import { User } from "@/core/modules/user/objects/User";
import { useStore } from "@/core/modules/store/module";

export default defineComponent({
  name: "KoruSortableBrowser",
  props: {
    module: { type: KoruSortableBrowserModule, default: undefined },
  },
  emits: ["update:module"],
  setup(props, { emit }) {
    const { t } = useLocale();
    const store = useStore();

    const user: User = store.getters[storeTypes.getters.getUser];

    const moduleRef = computed({
      get: () => props.module as KoruSortableBrowserModule<unknown>,
      set: (val) => emit("update:module", val),
    });

    const route = useRoute();
    const requiredModule: string =
      moduleRef.value.explicitRequiredModule !== undefined ? moduleRef.value.explicitRequiredModule : (route.meta.requiredModule as string);

    const handleLoad = async () => {
      await AppHelpers.tryOrError(
        async () => {
          await moduleRef.value.onMountedAction();
          await moduleRef.value.onLoadAction();
        },
        () => {
          if (moduleRef.value.loadingStop) store.commit(storeTypes.mutations.loadingStop);
        }
      );
    };

    const onReorder = async (event: Record<string, unknown>) => {
      await AppHelpers.tryOrToast(
        async () => {
          moduleRef.value.documents.value = event.value as unknown[];
          await moduleRef.value.onReorderAction(event.value as unknown[]);
        },
        "reorder",
        t,
        undefined
      );
    };

    const deleteAction = async (document: FirestoreDocument) => {
      await AppHelpers.tryOrToast(
        async () => {
          store.commit(storeTypes.mutations.loadingStart);
          const result: boolean = await moduleRef.value.onDeleteAction(document);

          if (result === true) {
            const deleteIndex = moduleRef.value.documents.value.indexOf(document, 0);
            if (deleteIndex > -1) {
              moduleRef.value.documents.value.splice(deleteIndex, 1);
            }
          } else {
            ToastHelpers.showToastWithError("delete", undefined, t);
          }
        },
        "delete",
        t,
        () => store.commit(storeTypes.mutations.loadingStop)
      );
    };

    const createAction = () => {
      if (moduleRef.value.createPath !== undefined) {
        Navigation.navigateTo(moduleRef.value.createPath);
      } else {
        Navigation.navigateTo(`${moduleRef.value.path}/new/edit`);
      }
    };

    const showDeleteItemDialog = ref(false);
    const deleteItem: Ref<FirestoreDocument | undefined> = ref(undefined);

    const deleteItemAction = () => {
      showDeleteItemDialog.value = false;
      if (deleteItem.value === undefined) return;
      deleteAction(deleteItem.value);
    };

    const getContextCommands = (document: FirestoreDocument) => {
      const renderContextCommands: ContextCommand[] = [];

      if (moduleRef.value.canView && (requiredModule == undefined || user.canRead(requiredModule, document) !== false)) {
        renderContextCommands.push({
          label: t("buttons.view"),
          icon: "pi pi-eye",
          command: (object: Record<string, unknown>) => {
            Navigation.navigateTo(`${moduleRef.value.path}/${(object.reference as Record<string, unknown>).id}`);
          },
        } as ContextCommand);
      }

      if (moduleRef.value.canEdit && (requiredModule == undefined || user.canUpdate(requiredModule, document) !== false)) {
        renderContextCommands.push({
          label: t("buttons.edit"),
          icon: "pi pi-pencil",
          command: (object: Record<string, unknown>) => {
            Navigation.navigateTo(`${moduleRef.value.path}/${(object.reference as Record<string, unknown>).id}/edit`);
          },
        } as ContextCommand);
      }

      if (moduleRef.value.canDelete && (requiredModule == undefined || user.canDelete(requiredModule, document) !== false)) {
        renderContextCommands.push({
          label: t("buttons.delete"),
          icon: "pi pi-trash",
          command: (object: Record<string, unknown>) => {
            deleteItem.value = object.reference as FirestoreDocument;
            showDeleteItemDialog.value = true;
          },
        } as ContextCommand);
      }

      if (moduleRef.value.contextCommands !== undefined && moduleRef.value.contextCommands.length > 0) {
        const contextCommandsLength = renderContextCommands.length;
        for (const contextCommand of moduleRef.value.contextCommands) {
          if (contextCommand.condition(document) == true) {
            if (renderContextCommands.length == contextCommandsLength && contextCommandsLength > 0) {
              renderContextCommands.push({ label: "separator" } as ContextCommand);
            }
            renderContextCommands.push(contextCommand);
          }
        }
      }

      if (renderContextCommands.length === 0) {
        renderContextCommands.push({
          label: t("buttons.noAction"),
          icon: "pi pi-times",
          condition: () => true,
          command: () => {
            return;
          },
        } as ContextCommand);
      }

      return renderContextCommands;
    };

    onMounted(() => {
      handleLoad();
    });

    return {
      createAction,
      DataHelpers,
      deleteItemAction,
      deleteItem,
      getContextCommands,
      moduleRef,
      onReorder,
      requiredModule,
      showDeleteItemDialog,
      t,
      user,
    };
  },
});
